import React, { useEffect, useState, useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BotonVolver from "../../../components/BotonVolver";
import ComparativaAnioPasado from "./ComparativaAnioPasado";
import ComparadorAnioPasado from "./ComparadorAnioPasado";

// Página administrativa: Análisis Datos
// - Tres pestañas (por ahora sólo la 1 tiene contenido: gráfica)
// - Filtros: supervisora (Todos) y rango de fechas
// - Gráfica de líneas con una línea por supervisora y una línea media resaltada

const COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
  '#17becf', '#bcbd22', '#393b79', '#637939', '#8c6d31', '#843c39', '#7b4173', '#5254a3',
  '#9c9ede', '#8ca252', '#b5cf6b', '#cedb9c'
];

// Centralized category color map — used for cards, traces, legend and pie
const CATEGORY_COLORS = {
  'Snacks': { color: 'rgba(177, 211, 25, 1)', fill: 'rgba(210, 236, 92, 1)', solid: 'rgba(210, 236, 92, 1)' },
  'Básico': { color: 'rgba(14, 185, 86, 0.6)', fill: 'rgba(19, 240, 111, 0.6)', solid: 'rgba(19, 240, 111, 0.6)' },
  'Proteína': { color: 'rgb(239,85,59)', fill: 'rgba(239,85,59,0.6)', solid: 'rgba(239,85,59,0.9)' },
  'Limpieza': { color: 'rgb(99,110,250)', fill: 'rgba(99,110,250,0.6)', solid: 'rgba(99,110,250,0.9)' },
  'Otros': { color: 'rgb(120,120,120)', fill: 'rgba(120,120,120,0.6)', solid: 'rgba(120,120,120,0.9)' }
};
// alias display name for 'Otros' -> 'Sin etiquetar' so we can show the new label
CATEGORY_COLORS['Sin etiquetar'] = CATEGORY_COLORS['Otros'];

function formatDate(d) {
  if (!d) return '';
  const t = new Date(d);
  return t.toISOString().slice(0,10);
}

// We'll use axios for API calls (keeps consistency with other pages)

const SmallTabs = ({ tabs, active, onChange }) => (
  <div style={{ display: 'flex', gap: 12, marginBottom: 16, justifyContent: 'center', width: '100%', flexWrap: 'wrap' }}>
    {tabs.map((t, i) => (
      <button
        key={t}
        onClick={() => onChange(i)}
        style={{
          padding: '12px 18px',
          borderRadius: 999,
          border: active === i ? 'none' : '1px solid #91b2ebff',
          background: active === i ? 'linear-gradient(90deg,#0ea5e9,#7c3aed)' : '#f8fafc',
          color: active === i ? '#fff' : '#0f172a',
          boxShadow: active === i ? '0 10px 30px rgba(59,130,246,0.18)' : 'none',
          fontSize: 15,
          minWidth: 160,
          textAlign: 'center',
          cursor: 'pointer',
          fontWeight: 800,
        }}
      >
        {t}
      </button>
    ))}
  </div>
);

// Simple SVG line chart that accepts series with same-length values array
const LineChart = ({ weeks, series, meanSeries, colorMap }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(1000);
  const height = 420;
  const margin = { top: 20, right: 20, bottom: 70, left: 80 };
  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;

  useEffect(() => {
    const handle = () => {
      try {
        const w = containerRef.current ? containerRef.current.clientWidth : 1000;
        setWidth(Math.max(600, w));
      } catch (_) { setWidth(900); }
    };
    handle();
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  // compute Y scale
  const allValues = [];
  series.forEach(s => s.values.forEach(v => { if (v !== null && v !== undefined) allValues.push(v); }));
  if (meanSeries) meanSeries.values.forEach(v=>{ if (v!==null && v!==undefined) allValues.push(v); });
  const maxY = allValues.length ? Math.max(...allValues) : 1;

  const x = (i) => margin.left + (i / Math.max(1, weeks.length-1)) * innerW;
  const y = (v) => margin.top + innerH - ((v||0) / Math.max(0.0001, maxY)) * innerH;

  // tooltip state
  const [tooltip, setTooltip] = useState(null);
  const [hovered, setHovered] = useState(null);

  // compute tooltip layout to avoid internal scrolling
  const TOOLTIP_ITEM_H = 28;
  const TOOLTIP_HEADER_H = 56; // includes week + media line
  const TOOLTIP_FOOTER_H = 28;
  const TOOLTIP_MAX_H = Math.max(160, height - 40);
  const TOOLTIP_W = 300;

  const handleMouseMove = (evt) => {
    try {
      const rect = evt.currentTarget.getBoundingClientRect();
      const mx = evt.clientX - rect.left;
      const my = evt.clientY - rect.top;

      // find nearest point among all series
      let best = { dist: Infinity, sIdx: -1, iIdx: -1, px: 0, py: 0 };
      for (let sIdx = 0; sIdx < series.length; sIdx++) {
        const s = series[sIdx];
        for (let i = 0; i < s.values.length; i++) {
          const v = s.values[i];
          if (v === null || v === undefined) continue;
          const px = x(i);
          const py = y(v);
          const dx = mx - px;
          const dy = my - py;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < best.dist) best = { dist, sIdx, iIdx: i, px, py };
        }
      }

      // threshold in px
      const TH = 14;
      if (best.dist <= TH) {
        // build tooltip items for that index
        const idx = best.iIdx;
        const items = series.map((s, si) => ({ name: s.name, value: s.values[idx], color: colorMap[s.name] || COLORS[si % COLORS.length] }))
          .filter(it => it.value !== null && it.value !== undefined)
          .sort((a,b)=> b.value - a.value);

        const meanVal = meanSeries && meanSeries.values && meanSeries.values[idx] ? meanSeries.values[idx] : null;
        setHovered({ idx, px: best.px, py: best.py });
        setTooltip({ x: best.px, y: best.py, week: weeks[idx], items, idx, meanVal });
      } else {
        setHovered(null);
        setTooltip(null);
      }
    } catch (e) { setTooltip(null); setHovered(null); }
  };
  // compute dynamic tooltip placement/size based on current tooltip state
  const tooltipHeight = tooltip ? Math.min(TOOLTIP_HEADER_H + (tooltip.items ? tooltip.items.length * TOOLTIP_ITEM_H : 0) + TOOLTIP_FOOTER_H, TOOLTIP_MAX_H) : 0;
  const tooltipXPos = tooltip ? Math.min(tooltip.x + 12, Math.max(margin.left + 8, width - TOOLTIP_W - 10)) : 0;
  const tooltipYPos = tooltip ? Math.max(margin.top + 6, Math.min(tooltip.y, height - tooltipHeight - 6)) : 0;

  // Make horizontally scrollable (mobile-friendly) with virtual width based on weeks
  const virtualWidth = Math.max(width, Math.min(1600, 90 + weeks.length * 70));

  return (
    <div ref={containerRef} style={{ width: '100%', overflowX: 'auto' }}>
      <svg width={virtualWidth} height={height} style={{ background: '#fff', border: '1px solid #eee' }} onMouseMove={handleMouseMove} onMouseLeave={() => { setTooltip(null); setHovered(null); }}>
        {/* axes */}
        <g>
          {/* Y grid + labels */}
          {[0,0.25,0.5,0.75,1].map((t,i)=>{
            const val = t * maxY;
            const yy = y(val);
            return (
              <g key={i}>
                <line x1={margin.left} x2={width-margin.right} y1={yy} y2={yy} stroke="#eee" />
                <text x={margin.left-10} y={yy+4} fontSize={12} textAnchor="end">{val ? val.toFixed(2) : '0'}</text>
              </g>
            );
          })}
        </g>

        {/* series lines */}
        {series.map((s, si) => {
          const color = colorMap && colorMap[s.name] ? colorMap[s.name] : COLORS[si % COLORS.length];
          
          // Crear segmentos de línea separados para evitar conexiones en valores null
          const segments = [];
          let currentSegment = [];
          
          s.values.forEach((v, i) => {
            if (v === null || v === undefined) {
              // Si hay un segmento en curso, guardarlo
              if (currentSegment.length > 0) {
                segments.push(currentSegment);
                currentSegment = [];
              }
            } else {
              // Agregar punto al segmento actual
              currentSegment.push({ index: i, value: v });
            }
          });
          
          // Guardar el último segmento si existe
          if (currentSegment.length > 0) {
            segments.push(currentSegment);
          }
          
          // Generar paths para cada segmento
          return (
            <g key={s.name}>
              {segments.map((segment, segIdx) => {
                if (segment.length === 0) return null;
                
                const path = segment.map((point, idx) => {
                  const cmd = idx === 0 ? 'M' : 'L';
                  return `${cmd} ${x(point.index)} ${y(point.value)}`;
                }).join(' ');
                
                return (
                  <path 
                    key={`${s.name}-seg-${segIdx}`}
                    d={path} 
                    fill="none" 
                    stroke={color} 
                    strokeWidth={2} 
                    strokeOpacity={0.95} 
                  />
                );
              })}
            </g>
          );
        })}

        {/* mean line thicker */}
        {meanSeries && (
          <g>
            {meanSeries.values && (() => {
              // Aplicar la misma lógica de segmentos para la línea media
              const segments = [];
              let currentSegment = [];
              
              (meanSeries.values || []).forEach((v, i) => {
                if (v === null || v === undefined) {
                  if (currentSegment.length > 0) {
                    segments.push(currentSegment);
                    currentSegment = [];
                  }
                } else {
                  currentSegment.push({ index: i, value: v });
                }
              });
              
              if (currentSegment.length > 0) {
                segments.push(currentSegment);
              }
              
              return segments.map((segment, segIdx) => {
                if (segment.length === 0) return null;
                
                const path = segment.map((point, idx) => {
                  const cmd = idx === 0 ? 'M' : 'L';
                  return `${cmd} ${x(point.index)} ${y(point.value)}`;
                }).join(' ');
                
                return (
                  <path 
                    key={`mean-seg-${segIdx}`}
                    d={path} 
                    fill="none" 
                    stroke="#111" 
                    strokeWidth={4} 
                    strokeOpacity={0.95} 
                  />
                );
              });
            })()}
          </g>
        )}

        {/* points */}
        {series.map((s, si) => {
          const color = colorMap && colorMap[s.name] ? colorMap[s.name] : COLORS[si % COLORS.length];
          return s.values.map((v, i) => {
            if (v === null || v === undefined) return null;
            const px = x(i); const py = y(v);
            const isHovered = hovered && hovered.idx === i && tooltip && tooltip.items.some(t=>t.name===s.name);
            return (
              <circle key={`${s.name}-${i}`} cx={px} cy={py} r={isHovered ? 5.5 : 3.5} fill={color} stroke="#fff" strokeWidth={1} />
            );
          });
        })}

        {/* X labels */}
        {weeks.map((w,i)=> (
          <g key={w} transform={`translate(${x(i)}, ${height-margin.bottom+8})`}>
            <text x={0} y={0} fontSize={12} textAnchor="end" transform="rotate(-55)">{w}</text>
          </g>
        ))}

        {/* tooltip marker */}
        {tooltip && (
          <g>
            <rect x={tooltip.x-1} y={margin.top} width={2} height={innerH} fill="#000" opacity={0.04} />
            <foreignObject x={tooltipXPos} y={tooltipYPos} width={TOOLTIP_W} height={tooltipHeight}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ background: '#fff', border: '1px solid #ddd', padding: 10, borderRadius: 8, fontSize: 13, boxShadow: '0 6px 24px rgba(0,0,0,0.12)', overflow: 'visible' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: 800 }}>{tooltip.week}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{tooltip.items.length} valores</div>
                </div>
                <div style={{ marginBottom: 8, fontSize: 13, color: '#111' }}>
                  <strong>Media:</strong> {tooltip.meanVal !== null && tooltip.meanVal !== undefined ? Number(tooltip.meanVal).toFixed(2) : '—'}
                </div>
                <div>
                  {tooltip.items.length === 0 ? <div style={{ color: '#666' }}>Sin datos</div> : tooltip.items.map(it=> (
                    <div key={it.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px dashed #eee' }}>
                      <div><span style={{ width: 10, height: 10, background: it.color, display: 'inline-block', marginRight: 8 }}></span>{it.name}</div>
                      <div style={{ fontWeight: 800 }}>{Number(it.value).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </foreignObject>
          </g>
        )}

      </svg>
    </div>
  );
};

const AdminAnalisisDatos = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [compararPasado, setCompararPasado] = useState(false);
  const [selectedSupervisoras, setSelectedSupervisoras] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [topN, setTopN] = useState(10);
  // applied (staged) filters: these are the values actually used when user clicks "Aplicar"
  const [appliedSupervisoras, setAppliedSupervisoras] = useState([]);
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [appliedTopN, setAppliedTopN] = useState(10);

  // Supervisoras marcadas para la media en el panel de configuración superadmin
  // (Servicios > Configuración > Media de supervisoras). Sustituye al antiguo
  // mecanismo de "favoritos" guardado en localStorage: ahora la media que se
  // muestra es siempre la que calcula el backend con estas supervisoras.
  const [supervisorasMedia, setSupervisorasMedia] = useState([]);
  const categoriasPlotWrapperRef = useRef(null);

  useEffect(() => {
    axios.get('/api/servicio/opciones_supervisora_media')
      .then((r) => {
        const filas = r.data?.supervisoras || [];
        setSupervisorasMedia(filas.filter((f) => f.media).map((f) => f.user));
      })
      .catch(() => setSupervisorasMedia([]));
  }, []);
  // custom dropdown state for supervisoras (desplegable con checkboxes)
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const [users, setUsers] = useState([]);
  const [analisisData, setAnalisisData] = useState(null);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);
  const [analisisError, setAnalisisError] = useState(null);
  const [topPisos, setTopPisos] = useState([]);
  const [loadingTopPisos, setLoadingTopPisos] = useState(false);
  const [topPisosError, setTopPisosError] = useState(null);
  const [selectedSupervisorSingle, setSelectedSupervisorSingle] = useState('');
  const [distribData, setDistribData] = useState(null);
  const [loadingDistrib, setLoadingDistrib] = useState(false);
  const [distribError, setDistribError] = useState(null);

  // NEW STATE for tab 3 (stacked area by category)
  const [categoriasData, setCategoriasData] = useState(null);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [categoriasError, setCategoriasError] = useState(null);
  const [selectedSupervisoraCateg, setSelectedSupervisoraCateg] = useState('');
  const [availablePisosCateg, setAvailablePisosCateg] = useState([]);
  const [selectedPisosCateg, setSelectedPisosCateg] = useState([]);
  const [compareMode, setCompareMode] = useState('none');
  const [compareSupervisora, setCompareSupervisora] = useState('');
  const [comparePiso, setComparePiso] = useState('');
  const [compareMeanSupervisoras, setCompareMeanSupervisoras] = useState([]);
  const [hoverWeekIndex, setHoverWeekIndex] = useState(null);
  const [hoverPiePosition, setHoverPiePosition] = useState({ x: 0, y: 0 });
  // applied filters for tab 3 (updated when user clicks Aplicar)
  const [appliedSupervisoraCateg, setAppliedSupervisoraCateg] = useState('');
  const [appliedPisosCateg, setAppliedPisosCateg] = useState([]);

  // helper to clear all filters (shared across the component)
  const handleClearFilters = () => {
    setSelectedSupervisoras([]);
    setSelectedSupervisorSingle('');
    setStartDate('');
    setEndDate('');
    setTopN(10);
    setAppliedTopN(10);
    setSelectedSupervisoraCateg('');
    setSelectedPisosCateg([]);
    setAppliedSupervisoraCateg('');
    setAppliedPisosCateg([]);
    setCompareMode('none');
    setCompareSupervisora('');
    setComparePiso('');
    setCompareMeanSupervisoras([]);
    applyFilters({ top: 10, supervisora: [], start_date: '', end_date: '' });
    if (activeTab === 3) {
      fetchCategorias({ supervisora: '', pisos: [], start_date: '', end_date: '' }, { skipCompare: true });
    }
    setDropdownOpen(false);
  };

  // ref used for debugging mouse events over the categorias Plot wrapper
  const lastPlotlyHoverRef = useRef(0);





  useEffect(() => {
    const fetchSupervisoras = async () => {
      try {
        const r = await axios.get('/api/pisos/usuarios-por-tipo', { withCredentials: true });
        const d = r.data || {};
        const sups = (d.supervisoras || []).map(u => u.user);
        setUsers(sups);
      } catch (e) {
        console.warn('Error cargando supervisoras', e);
        setUsers([]);
      }
    };
    fetchSupervisoras();
  }, []);

  // Media general: siempre la que calcula el backend (ya filtrada por las
  // supervisoras marcadas en `opciones_supervisora_media`, ver analisis_datos.py).
  const meanTotal = useMemo(() => {
    if (!analisisData || !analisisData.mean || !Array.isArray(analisisData.mean.values)) return null;
    const vals = analisisData.mean.values.filter(v => v !== null && v !== undefined);
    if (vals.length === 0) return null;
    return vals.reduce((a,b) => a + b, 0) / vals.length;
  }, [analisisData]);

  // suma de las medias semanales (usado para el encabezado del Top-10)
  const selectedMean = useMemo(() => {
    if (!analisisData || !analisisData.mean || !Array.isArray(analisisData.mean.values)) return null;
    const vals = analisisData.mean.values.filter(v => v !== null && v !== undefined);
    if (vals.length === 0) return null;
    return vals.reduce((a,b) => a + b, 0);
  }, [analisisData]);

  // weekly average mean: selectedMean divided by number of weeks
  const selectedMeanWeekly = useMemo(() => {
    if (selectedMean === null || selectedMean === undefined || !analisisData || !Array.isArray(analisisData.weeks)) return null;
    const weeksCount = analisisData.weeks.length;
    if (!weeksCount) return null;
    return selectedMean / weeksCount;
  }, [selectedMean, analisisData]);

  // fetchOverrides: optional { top, supervisora, start_date, end_date } to force values for the subsequent top-pisos fetch
  const applyFilters = async (fetchOverrides = {}) => {
    // determine applied values: prefer overrides when provided, otherwise use current drafts
    const newAppliedSupervisoras = fetchOverrides.supervisora !== undefined
      ? (Array.isArray(fetchOverrides.supervisora) ? fetchOverrides.supervisora : (fetchOverrides.supervisora ? String(fetchOverrides.supervisora).split(',') : []))
      : selectedSupervisoras;
    const newAppliedStartDate = fetchOverrides.start_date !== undefined ? fetchOverrides.start_date : startDate;
    const newAppliedEndDate = fetchOverrides.end_date !== undefined ? fetchOverrides.end_date : endDate;
    const newAppliedTopN = fetchOverrides.top !== undefined ? fetchOverrides.top : (topN || 10);

    // apply them to state so UI updates immediately (title/table use appliedTopN)
    setAppliedSupervisoras(newAppliedSupervisoras);
    setAppliedStartDate(newAppliedStartDate);
    setAppliedEndDate(newAppliedEndDate);
    setAppliedTopN(newAppliedTopN);

    setLoadingAnalisis(true); setAnalisisError(null);
    try {
      const params = {};
      if (newAppliedStartDate) params.start_date = newAppliedStartDate;
      if (newAppliedEndDate) params.end_date = newAppliedEndDate;
      if (fetchOverrides.comparar !== undefined ? fetchOverrides.comparar : compararPasado) params.comparar = '1';
      const r = await axios.get('/api/analisis/gasto-semanal', { params, withCredentials: true });
      const full = r.data || { weeks: [], series: [], mean: { values: [] } };
      setAnalisisData(full);
      // refresh top pisos using the applied values (prefer explicit fetchOverrides, else use newApplied values)
      try {
        const overrides = {
          top: fetchOverrides.top !== undefined ? fetchOverrides.top : newAppliedTopN,
          supervisora: fetchOverrides.supervisora !== undefined ? fetchOverrides.supervisora : newAppliedSupervisoras,
          start_date: fetchOverrides.start_date !== undefined ? fetchOverrides.start_date : newAppliedStartDate,
          end_date: fetchOverrides.end_date !== undefined ? fetchOverrides.end_date : newAppliedEndDate,
        };
        await fetchTopPisos(overrides);
        // also fetch distribution data for the single supervisora view if requested
        try {
          // prefer explicit override supervisora (could be single string or array)
          let supForDistrib = undefined;
          if (fetchOverrides.supervisora !== undefined) {
            if (Array.isArray(fetchOverrides.supervisora)) supForDistrib = fetchOverrides.supervisora[0];
            else if (typeof fetchOverrides.supervisora === 'string') supForDistrib = fetchOverrides.supervisora.split(',')[0];
          } else if (newAppliedSupervisoras && newAppliedSupervisoras.length === 1) {
            supForDistrib = newAppliedSupervisoras[0];
          }
          // if we have a supervisora (single), fetch distrib; otherwise clear
          if (supForDistrib) {
            await fetchDistrib({ supervisora: supForDistrib, start_date: newAppliedStartDate, end_date: newAppliedEndDate });
            setSelectedSupervisorSingle(supForDistrib);
          } else {
            setDistribData(null);
          }
        } catch (_) {}
      } catch(_) {}
    } catch (e) {
      console.error('Error cargando analisis', e);
      setAnalisisError(e.response?.data?.error || String(e));
      setAnalisisData(null);
    } finally {
      setLoadingAnalisis(false);
    }
  };

  useEffect(()=>{ applyFilters(); }, []);

  // fetch top pisos: accepts an optional overrides object so we can fetch with staged/applied values
  const fetchTopPisos = async (overrides = {}) => {
    setLoadingTopPisos(true); setTopPisosError(null);
    try {
      const params = {};
      const topParam = overrides.top !== undefined ? overrides.top : appliedTopN;
      const supParam = overrides.supervisora !== undefined ? overrides.supervisora : (appliedSupervisoras || []);
      const startParam = overrides.start_date !== undefined ? overrides.start_date : appliedStartDate;
      const endParam = overrides.end_date !== undefined ? overrides.end_date : appliedEndDate;
      if (topParam) params.top = topParam;
      if (supParam && supParam.length > 0) params.supervisora = Array.isArray(supParam) ? supParam.join(',') : supParam;
      if (startParam) params.start_date = startParam;
      if (endParam) params.end_date = endParam;
      const r = await axios.get('/api/analisis/top-pisos', { params, withCredentials: true });
      setTopPisos(r.data?.top_pisos || []);
    } catch (e) {
      console.error('Error cargando top pisos', e);
      setTopPisosError(e.response?.data?.error || String(e));
      setTopPisos([]);
    } finally {
      setLoadingTopPisos(false);
    }
  };

  // fetch distribution per-piso for a given supervisora and date range
  const fetchDistrib = async (overrides = {}) => {
    setLoadingDistrib(true); setDistribError(null);
    try {
      const params = {};
      const supParam = overrides.supervisora !== undefined ? overrides.supervisora : (appliedSupervisoras && appliedSupervisoras.length === 1 ? appliedSupervisoras[0] : undefined);
      const startParam = overrides.start_date !== undefined ? overrides.start_date : appliedStartDate;
      const endParam = overrides.end_date !== undefined ? overrides.end_date : appliedEndDate;
      if (supParam) params.supervisora = supParam;
      if (startParam) params.start_date = startParam;
      if (endParam) params.end_date = endParam;
      const r = await axios.get('/api/analisis/distribucion-supervisora', { params, withCredentials: true });
      setDistribData(r.data || { rows: [], weeks: [] });
    } catch (e) {
      console.error('Error cargando distribucion', e);
      setDistribError(e.response?.data?.error || String(e));
      setDistribData(null);
    } finally {
      setLoadingDistrib(false);
    }
  };

  // fetch categorias data for tab 3
  const fetchCategorias = async (overrides = {}, options = {}) => {
    const skipCompare = options.skipCompare || false;
    setLoadingCategorias(true);
    setCategoriasError(null);
    
    try {
      const params = {};
      const supParam = overrides.supervisora !== undefined ? overrides.supervisora : selectedSupervisoraCateg;
      const pisosParam = overrides.pisos !== undefined ? overrides.pisos : selectedPisosCateg;
      const startParam = overrides.start_date !== undefined ? overrides.start_date : (appliedStartDate || startDate);
      const endParam = overrides.end_date !== undefined ? overrides.end_date : (appliedEndDate || endDate);
      
      if (supParam) params.supervisora = supParam;
      if (pisosParam && pisosParam.length > 0) params.piso = pisosParam.join(',');
      if (startParam) params.start_date = startParam;
      if (endParam) params.end_date = endParam;
      
      const r = await axios.get('/api/analisis/gasto-semanal-categorias', { params, withCredentials: true });
      const data = r.data || { weeks: [], series: {}, pisos: [] };
      setCategoriasData(data);
      
      if (supParam) {
        setAvailablePisosCateg(data.pisos || []);
      } else {
        setAvailablePisosCateg([]);
      }
      
      // Fetch comparison data
      if (!skipCompare && compareMode === 'supervisora' && compareSupervisora) {
        const compareParams = { ...params };
        delete compareParams.supervisora;
        delete compareParams.piso;
        compareParams.supervisora = compareSupervisora;
        const rComp = await axios.get('/api/analisis/gasto-semanal-categorias', { params: compareParams, withCredentials: true });
        setCategoriasData(prev => ({ ...prev, compare: rComp.data }));
      } else if (!skipCompare && compareMode === 'piso' && comparePiso) {
        const compareParams = { ...params };
        delete compareParams.supervisora;
        delete compareParams.piso;
        compareParams.piso = comparePiso;
        const rComp = await axios.get('/api/analisis/gasto-semanal-categorias', { params: compareParams, withCredentials: true });
        setCategoriasData(prev => ({ ...prev, compare: rComp.data }));
      } else if (!skipCompare && compareMode === 'media') {
        // Prefer an explicit compare-mean selection, then the supervisoras marcadas
        // en configuración (opciones_supervisora_media), then applied supervisoras
        const supList = (compareMeanSupervisoras && compareMeanSupervisoras.length > 0)
          ? compareMeanSupervisoras
          : ((supervisorasMedia && supervisorasMedia.length > 0) ? supervisorasMedia : (appliedSupervisoras || []));
        if (supList && supList.length > 0) {
          const meanPromises = supList.map(sup => 
            axios.get('/api/analisis/gasto-semanal-categorias', { 
              params: { ...params, supervisora: sup, piso: undefined }, 
              withCredentials: true 
            }).catch(() => ({ data: { weeks: [], series: { snacks: [], basico: [], proteina: [], limpieza: [] } } }))
          );
          const meanResults = await Promise.all(meanPromises);
          
          const meanSeries = { snacks: [], basico: [], proteina: [], limpieza: [] };
          const weeksCount = data.weeks.length;
          
          for (let i = 0; i < weeksCount; i++) {
            for (const cat of ['snacks', 'basico', 'proteina', 'limpieza']) {
              const vals = meanResults.map(r => r.data.series[cat][i]).filter(v => v !== null && v !== undefined);
              meanSeries[cat].push(vals.length ? vals.reduce((a,b) => a+b, 0) / vals.length : 0);
            }
          }
          
          setCategoriasData(prev => ({ ...prev, compare: { weeks: data.weeks, series: meanSeries } }));
        }
      }
      
    } catch (e) {
      console.error('Error cargando categorias', e);
      setCategoriasError(e.response?.data?.error || String(e));
      setCategoriasData(null);
    } finally {
      setLoadingCategorias(false);
    }
  };

  useEffect(() => {
    if (activeTab === 3) {
      fetchCategorias();
    }
  }, [activeTab]);

  // color mapping for supervisoras (stable ordering)
  const colorMap = useMemo(() => {
    const map = {};
    const list = users.slice();
    if (list.length === 0 && analisisData && analisisData.series) {
      list.push(...analisisData.series.map(s => s.name));
    }
    list.forEach((u, i) => { map[u] = COLORS[i % COLORS.length]; });
    return map;
  }, [users, analisisData]);

  // Media mostrada en el gráfico: siempre la que calcula el backend (ya
  // filtrada por las supervisoras marcadas en `opciones_supervisora_media`).
  const meanSeriesDisplayed = useMemo(() => analisisData?.mean || null, [analisisData]);

  // refetch top pisos when tab changes to 1 (use applied values)
  useEffect(() => {
    if (activeTab === 1) fetchTopPisos();
  }, [activeTab]);

  // when switching tabs, reflect the currently applied filters in the draft controls
  useEffect(() => {
    // copy applied filters into draft inputs so UI reflects what is actually applied
    setSelectedSupervisoras(Array.isArray(appliedSupervisoras) ? appliedSupervisoras.slice() : []);
    setStartDate(appliedStartDate || '');
    setEndDate(appliedEndDate || '');
    setTopN(appliedTopN || 10);
    // set the single-select draft for tab 3 to the applied supervisor if exactly one
    setSelectedSupervisorSingle((appliedSupervisoras && appliedSupervisoras.length === 1) ? appliedSupervisoras[0] : '');
  }, [activeTab, appliedSupervisoras, appliedStartDate, appliedEndDate, appliedTopN]);

  // Fallback: if Plotly does not emit hover (mouse over filled areas), approximate the week index using pointer position
  useEffect(() => {
    if (activeTab !== 3) return undefined;
    const handler = (e) => {
      try {
        if (!categoriasPlotWrapperRef || !categoriasPlotWrapperRef.current) return;
        const rect = categoriasPlotWrapperRef.current.getBoundingClientRect();
        const inside = (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom);
        if (!inside) {
          if (hoverWeekIndex !== null && (Date.now() - lastPlotlyHoverRef.current) > 200) {
            setHoverWeekIndex(null);
            setHoverPiePosition({ x: 0, y: 0 });
          }
          return;
        }
        const now = Date.now();
        if (now - lastPlotlyHoverRef.current < 120) return;
        const sourceWeeks = (categoriasData && Array.isArray(categoriasData.weeks)) ? categoriasData.weeks : [];
        const analWeeks = (Array.isArray(sourceWeeks) && sourceWeeks.length)
          ? sourceWeeks
          : ((analisisData && Array.isArray(analisisData.weeks)) ? analisisData.weeks : []);
        if (!Array.isArray(analWeeks) || analWeeks.length === 0) return;
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / Math.max(1, rect.width)));
        const idx = Math.min(analWeeks.length - 1, Math.max(0, Math.round(ratio * (analWeeks.length - 1))));
        setHoverWeekIndex(idx);
        setHoverPiePosition({ x: e.clientX, y: e.clientY });
      } catch (err) { /* ignore */ }
    };
    document.addEventListener('mousemove', handler);
    return () => document.removeEventListener('mousemove', handler);
  }, [activeTab, categoriasData, analisisData, hoverWeekIndex]);

  // Render helper for Tab 3 (categorias)
  const renderCategoriasChart = () => {
    if (!categoriasData || !categoriasData.weeks || categoriasData.weeks.length === 0) return null;
    const { weeks: sourceWeeks, series: sourceSeries, compare } = categoriasData;
    // Normalize weeks to the intersection with Tab 1 weeks so averages are computed over identical windows.
    const analWeeksRaw = (Array.isArray(sourceWeeks) && sourceWeeks.length) ? sourceWeeks : ((analisisData && Array.isArray(analisisData.weeks)) ? analisisData.weeks : []);
    const tab1Weeks = (analisisData && Array.isArray(analisisData.weeks)) ? analisisData.weeks : [];
    const analWeeks = Array.isArray(analWeeksRaw) && analWeeksRaw.length
      ? analWeeksRaw.filter(w => tab1Weeks.length === 0 || tab1Weeks.includes(w))
      : [];
    const prepareSeries = (cat) => analWeeks.map(w => {
      const idx = sourceWeeks.indexOf(w);
      return (idx >= 0 && sourceSeries && Array.isArray(sourceSeries[cat]) ) ? (sourceSeries[cat][idx] || 0) : 0;
    });
    const s_snacks = prepareSeries('snacks');
    const s_basico = prepareSeries('basico');
    const s_proteina = prepareSeries('proteina');
    const s_limpieza = prepareSeries('limpieza');
    const s_otros = prepareSeries('otros');
    // placeholders for compare series (filled if categoriasData.compare exists)
    let c_snacks = null, c_basico = null, c_proteina = null, c_limpieza = null, c_otros = null;
    let compareLabel = null;
    const meanOf = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return 0;
      const vals = arr.filter(v => v !== null && v !== undefined);
      if (vals.length === 0) return 0;
      return vals.reduce((a,b) => a + Number(b || 0), 0) / vals.length;
    };
    const avg_snacks = meanOf(s_snacks);
    const avg_basico = meanOf(s_basico);
    const avg_proteina = meanOf(s_proteina);
    const avg_limpieza = meanOf(s_limpieza);
    const avg_otros = meanOf(s_otros);
    // compare averages placeholders (will be computed if compare series present)
    const cmpAvg = (arr) => { if (!Array.isArray(arr) || arr.length===0) return 0; const vals = arr.map(v => (v||0)); return vals.reduce((a,b)=>a+b,0)/vals.length };
    let cmp_avg_snacks = null, cmp_avg_basico = null, cmp_avg_proteina = null, cmp_avg_limpieza = null, cmp_avg_otros = null;
    let cmpTotalAvg = null;
    // Compute "gasto semanal promedio por alumno" como media de la suma semanal de categorías (igual que Tab 1)
    // IMPORTANT: s_* arrays are already aligned to analWeeks (not sourceWeeks), so sum by local index
    const weeklyTotals = analWeeks.map((_, i) => {
      const a = s_snacks[i] || 0;
      const b = s_basico[i] || 0;
      const c = s_proteina[i] || 0;
      const d = s_limpieza[i] || 0;
      const e = s_otros[i] || 0;
      return a + b + c + d + e;
    });
    const totalAvg = meanOf(weeklyTotals);
    // percentage helper (unused for now)
    // const pct = (v) => totalAvg ? (v / totalAvg * 100) : 0;

    // decide whether to show the "Sin etiquetar" category: if the main series is all zeros, omit it everywhere
    const showSinEtiquetar = Array.isArray(s_otros) && s_otros.some(v => (v || 0) > 0);
    const traces = [];
    traces.push({ x: analWeeks, y: s_snacks, name: 'Snacks', type: 'scatter', mode: 'lines', stackgroup: 'one', fillcolor: CATEGORY_COLORS['Snacks'].fill, line: { color: CATEGORY_COLORS['Snacks'].color, width: 2 }, hovertemplate: '%{y:.2f}€<extra></extra>', hoveron: 'fills+points', hoverdistance: 60 });
    traces.push({ x: analWeeks, y: s_basico, name: 'Básico', type: 'scatter', mode: 'lines', stackgroup: 'one', fillcolor: CATEGORY_COLORS['Básico'].fill, line: { color: CATEGORY_COLORS['Básico'].color, width: 2 }, hovertemplate: '%{y:.2f}€<extra></extra>', hoveron: 'fills+points', hoverdistance: 60 });
    traces.push({ x: analWeeks, y: s_proteina, name: 'Proteína', type: 'scatter', mode: 'lines', stackgroup: 'one', fillcolor: CATEGORY_COLORS['Proteína'].fill, line: { color: CATEGORY_COLORS['Proteína'].color, width: 2 }, hovertemplate: '%{y:.2f}€<extra></extra>', hoveron: 'fills+points', hoverdistance: 60 });
    traces.push({ x: analWeeks, y: s_limpieza, name: 'Limpieza', type: 'scatter', mode: 'lines', stackgroup: 'one', fillcolor: CATEGORY_COLORS['Limpieza'].fill, line: { color: CATEGORY_COLORS['Limpieza'].color, width: 2 }, hovertemplate: '%{y:.2f}€<extra></extra>', hoveron: 'fills+points', hoverdistance: 60 });
    if (showSinEtiquetar) {
      traces.push({ x: analWeeks, y: s_otros, name: 'Sin etiquetar', type: 'scatter', mode: 'lines', stackgroup: 'one', fillcolor: CATEGORY_COLORS['Sin etiquetar'].fill, line: { color: CATEGORY_COLORS['Sin etiquetar'].color, width: 2 }, hovertemplate: '%{y:.2f}€<extra></extra>', hoveron: 'fills+points', hoverdistance: 60 });
    }
    const compareActive = (compareMode && compareMode !== 'none' && compare && compare.series);
    // ensure compareWeeklyTotals is defined for downstream usage
    let compareWeeklyTotals = [];
    if (compareActive) {
      const cmpWeeks = compare.weeks || [];
      const prepareCompare = (cat) => analWeeks.map(w => {
        const idx = cmpWeeks.indexOf(w);
        return (idx >= 0 && compare.series && Array.isArray(compare.series[cat])) ? (compare.series[cat][idx] || 0) : 0;
      });
      c_snacks = prepareCompare('snacks');
      c_basico = prepareCompare('basico');
      c_proteina = prepareCompare('proteina');
      c_limpieza = prepareCompare('limpieza');
      c_otros = prepareCompare('otros');
      // c_* arrays are aligned to analWeeks via prepareCompare, sum by local index
      compareWeeklyTotals = analWeeks.map((_, i) => {
        const a = (Array.isArray(c_snacks) ? (c_snacks[i]||0) : 0);
        const b = (Array.isArray(c_basico) ? (c_basico[i]||0) : 0);
        const c = (Array.isArray(c_proteina) ? (c_proteina[i]||0) : 0);
        const d = (Array.isArray(c_limpieza) ? (c_limpieza[i]||0) : 0);
        const e = (Array.isArray(c_otros) ? (c_otros[i]||0) : 0);
        return a + b + c + d + e;
      });
      let compareName = 'Comparación';
      if (compareMode === 'media') compareName = 'Media';
      else if (compareMode === 'supervisora') compareName = `${compareSupervisora}`;
      else if (compareMode === 'piso') {
        const pisoObj = availablePisosCateg.find(p => String(p.id_piso) === String(comparePiso));
        compareName = pisoObj ? pisoObj.abreviatura_piso : `Piso ${comparePiso}`;
      }
      compareLabel = compareName;
      // Add per-category comparison traces so comparisons are shown by category, not only total
      // if main "Sin etiquetar" was all zeros we avoided adding the main trace; also skip compare category if compare series are all zeros
      const compareCategories = [];
      compareCategories.push({ key: 'snacks', display: 'Snacks', series: c_snacks });
      compareCategories.push({ key: 'basico', display: 'Básico', series: c_basico });
      compareCategories.push({ key: 'proteina', display: 'Proteína', series: c_proteina });
      compareCategories.push({ key: 'limpieza', display: 'Limpieza', series: c_limpieza });
      // add Sin etiquetar only if compare series has any non-zero or main showed it
      const showCmpSin = Array.isArray(c_otros) && c_otros.some(v => (v || 0) > 0);
      if (showSinEtiquetar || showCmpSin) compareCategories.push({ key: 'otros', display: 'Sin etiquetar', series: c_otros });

      // Render comparison as dashed lines per category, without fill and without hatch overlays.
      const order = ['snacks','basico','proteina','limpieza','otros'];
      // running cumulative totals for stacking so the traces align with main series sums if needed
      const zeros = Array(analWeeks.length).fill(0);
      let cumulative = zeros.slice();
      order.forEach(catKey => {
        const cat = compareCategories.find(c => c.key === catKey);
        if (!cat) return;
        const top = cumulative.map((c, i) => c + (cat.series[i] || 0));
        const colorKey = cat.display;
        const base = CATEGORY_COLORS[colorKey] ? CATEGORY_COLORS[colorKey] : { solid: 'rgba(120,120,120,0.9)' };
        // First add an invisible area trace to participate in stacking (keeps sums aligned).
        traces.push({
          x: analWeeks,
          y: cat.series,
          name: `${compareName} - ${cat.display}`,
          type: 'scatter',
          mode: 'lines',
          stackgroup: 'compare_group',
          fillcolor: 'rgba(0,0,0,0)',
          line: { color: 'rgba(0,0,0,0)', width: 0 },
          hoverinfo: 'skip',
          showlegend: false
        });

        // outline/backing trace: draw along the TOP (sumatorio) boundary so it outlines the stacked shape
        traces.push({
          x: analWeeks,
          y: top,
          name: `${compareName} - ${cat.display} (outline)`,
          type: 'scatter',
          mode: 'lines',
          line: { color: '#000000', width: 4, dash: 'solid' },
          opacity: 0.65,
          hoverinfo: 'skip',
          showlegend: false
        });

        // colored dashed line drawn along the TOP as well, with customdata set to the per-category values
        // so hover shows the actual category value even though the line is plotted at the cumulative top.
        traces.push({
          x: analWeeks,
          y: top,
          customdata: cat.series,
          name: `${compareName} - ${cat.display}`,
          type: 'scatter',
          mode: 'lines',
          line: { color: base.solid, width: 2, dash: 'dash' },
          marker: { size: 4, color: base.solid, opacity: 0.9 },
          hovertemplate: `%{customdata:.2f}€<extra>${compareName} - ${cat.display}</extra>`,
          showlegend: true
        });

        cumulative = top;
      });
    }

    let filterText = 'Todas las supervisoras';
    if (appliedSupervisoraCateg) {
      if (appliedPisosCateg && appliedPisosCateg.length > 0) {
        const names = appliedPisosCateg.map(id => {
          const p = availablePisosCateg.find(pp => String(pp.id_piso) === String(id));
          return p ? (p.abreviatura_piso || `#${p.id_piso}`) : `#${id}`;
        });
        filterText = `${appliedSupervisoraCateg}: ${names.join(', ')} de los pisos seleccionados`;
      } else {
        filterText = appliedSupervisoraCateg;
      }
    }

    if (compareActive) {
      // compute compare averages now that c_* series exist regardless of supervisora filter
      try {
        if (Array.isArray(c_snacks)) cmp_avg_snacks = cmpAvg(c_snacks);
        if (Array.isArray(c_basico)) cmp_avg_basico = cmpAvg(c_basico);
        if (Array.isArray(c_proteina)) cmp_avg_proteina = cmpAvg(c_proteina);
        if (Array.isArray(c_limpieza)) cmp_avg_limpieza = cmpAvg(c_limpieza);
        if (Array.isArray(c_otros)) cmp_avg_otros = cmpAvg(c_otros);
      } catch (e) { /* ignore */ }
      // compute compare total average as mean of weekly totals (align with Tab 1)
      cmpTotalAvg = cmpAvg(compareWeeklyTotals);
    }
    

    const layout = {
      title: { text: `Gasto semanal por alumno - Por categoría — ${filterText}` + (compareLabel ? ` — Comparativa: ${compareLabel}` : ''), font: { size: 18 } },
      xaxis: { title: 'Semana', tickangle: -45, tickmode: 'array', tickvals: analWeeks, ticktext: analWeeks.map(w => formatDate(w)), categoryorder: 'array', categoryarray: analWeeks },
      yaxis: { title: 'Precio real semanal por alumno (€)' },
      hovermode: 'x unified',
      height: 560,
      legend: { orientation: 'h', yanchor: 'bottom', y: -0.25, xanchor: 'center', x: 0.5 }
    };

    const formatAverageValue = (value) => (typeof value === 'number' && Number.isFinite(value)) ? `${value.toFixed(2)} €` : 'Sin datos';
    const mainAverageLabel = appliedSupervisoraCateg ? `Promedio ${appliedSupervisoraCateg}` : 'Promedio general';
    const hasCompareAverage = Boolean(compareLabel && (cmpTotalAvg || 0) > 0);
    const safeValue = (val) => (typeof val === 'number' && Number.isFinite(val)) ? val : null;
    // To match Tab 1 exactly when a single supervisora is selected, compute the mean directly from Tab 1 series for that supervisora.
    let mainAverageValue = safeValue(totalAvg);
    if (appliedSupervisoraCateg && analisisData && Array.isArray(analisisData.series)) {
      try {
        const wanted = String(appliedSupervisoraCateg).trim().toLowerCase();
        const entry = analisisData.series.find(s => String(s.name).trim().toLowerCase() === wanted);
        if (entry && Array.isArray(entry.values)) {
          const vals = entry.values.filter(v => v !== null && v !== undefined).map(Number);
          if (vals.length) mainAverageValue = vals.reduce((a,b)=>a+b,0) / vals.length;
        }
      } catch (_) { /* ignore */ }
    } else {
      // Promedio general: usa el mismo criterio que Tab 1 (meanTotal) si está disponible
      if (typeof meanTotal === 'number' && Number.isFinite(meanTotal)) {
        mainAverageValue = meanTotal;
      }
    }
    // For compare average, if comparing another supervisora, mirror Tab 1 series mean too
    let compareAverageValue = safeValue(cmpTotalAvg);
    if (compareLabel && compareMode === 'supervisora' && compareSupervisora && analisisData && Array.isArray(analisisData.series)) {
      try {
        const wantedCmp = String(compareSupervisora).trim().toLowerCase();
        const entryCmp = analisisData.series.find(s => String(s.name).trim().toLowerCase() === wantedCmp);
        if (entryCmp && Array.isArray(entryCmp.values)) {
          const vals = entryCmp.values.filter(v => v !== null && v !== undefined).map(Number);
          if (vals.length) compareAverageValue = vals.reduce((a,b)=>a+b,0) / vals.length;
        }
      } catch (_) { /* ignore */ }
    }
    const mainIsLeader = hasCompareAverage ? ((mainAverageValue || 0) >= (compareAverageValue || 0)) : true;
    const compareIsLeader = hasCompareAverage ? ((compareAverageValue || 0) > (mainAverageValue || 0)) : false;
    const buildSummaryCardPalette = (isLeader) => ({
      container: {
        minWidth: 240,
        padding: '12px 18px',
        borderRadius: 12,
        border: isLeader ? '1px solid #ffe4e6' : '1px solid #e2e8f0',
        background: isLeader ? '#fdf2f8' : '#f8fafc',
        boxShadow: isLeader ? '0 8px 20px rgba(190,24,93,0.15)' : '0 8px 18px rgba(15,23,42,0.06)',
        textAlign: 'center'
      },
      titleColor: isLeader ? '#831843' : '#0b1220',
      valueColor: isLeader ? '#9d174d' : '#0f172a',
      subtitleColor: isLeader ? '#831843' : '#475569'
    });
    const mainCardPalette = buildSummaryCardPalette(mainIsLeader);
    const compareCardPalette = buildSummaryCardPalette(compareIsLeader);

    return (
      <div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
                        <div style={mainCardPalette.container}>
                          <div style={{ fontWeight: 800, color: mainCardPalette.titleColor, fontSize: 14 }}>Gasto medio semanal por alumno</div>
                          <div style={{ fontSize: 28, fontWeight: 900, color: mainCardPalette.valueColor, marginTop: 4 }}>{formatAverageValue(mainAverageValue)}</div>
                          <div style={{ fontSize: 12, color: mainCardPalette.subtitleColor, marginTop: 4 }}>{mainAverageLabel}</div>
                        </div>
                        {hasCompareAverage && (
                          <div style={compareCardPalette.container}>
                            <div style={{ fontWeight: 800, color: compareCardPalette.titleColor, fontSize: 14 }}>{`Comparativa: ${compareLabel}`}</div>
                            <div style={{ fontSize: 28, fontWeight: 900, color: compareCardPalette.valueColor, marginTop: 4 }}>{formatAverageValue(compareAverageValue)}</div>
                            <div style={{ fontSize: 12, color: compareCardPalette.subtitleColor, marginTop: 4 }}>Gasto medio semanal por alumno</div>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                        {(() => {
                          const labels = ['Snacks', 'Básico', 'Proteína', 'Limpieza'];
                          const includeSin = ((avg_otros || 0) > 0) || ((cmp_avg_otros || 0) > 0);
                          if (includeSin) labels.push('Sin etiquetar');
                          const colorList = labels.map(l => (CATEGORY_COLORS[l] ? CATEGORY_COLORS[l].solid : '#9ca3af'));
                          // Compute pie values so that their sum == displayed average (Tab 1-aligned when single supervisora)
                          const refAvg = (typeof mainAverageValue === 'number' && Number.isFinite(mainAverageValue)) ? mainAverageValue : totalAvg;
                          const shareOf = (arrVals, totals) => {
                            if (!Array.isArray(arrVals) || !Array.isArray(totals) || totals.length === 0) return 0;
                            let sum = 0, cnt = 0;
                            for (let i = 0; i < totals.length; i++) {
                              const tot = Number(totals[i] || 0);
                              const v = Number((arrVals[i] || 0));
                              if (tot > 0 && Number.isFinite(tot)) { sum += (v / tot); cnt++; }
                            }
                            return cnt > 0 ? (sum / cnt) : 0;
                          };
                          const seriesMap = { 'Snacks': s_snacks, 'Básico': s_basico, 'Proteína': s_proteina, 'Limpieza': s_limpieza, 'Sin etiquetar': s_otros };
                          const mainShares = labels.map(l => shareOf(seriesMap[l] || [], weeklyTotals));
                          const mainValues = mainShares.map(sh => (refAvg || 0) * sh);
                          const totalMain = mainValues.reduce((acc, v) => acc + (v || 0), 0);
                          // Comparison pie uses the compare series and its own average (Tab1-aligned when applicable)
                          const cmpRefAvg = (typeof compareAverageValue === 'number' && Number.isFinite(compareAverageValue)) ? compareAverageValue : (cmpTotalAvg || 0);
                          const cmpSeriesMap = { 'Snacks': c_snacks, 'Básico': c_basico, 'Proteína': c_proteina, 'Limpieza': c_limpieza, 'Sin etiquetar': c_otros };
                          const compareHasTwoPies = Boolean(compareLabel && (cmpRefAvg || 0) > 0);
                          const cmpShares = compareHasTwoPies ? labels.map(l => shareOf(cmpSeriesMap[l] || [], compareWeeklyTotals || [])) : [];
                          const cmpValues = compareHasTwoPies ? cmpShares.map(sh => (cmpRefAvg || 0) * sh) : [];
                          const totalCmp = compareHasTwoPies ? cmpValues.reduce((acc, v) => acc + (v || 0), 0) : 0;
                          const mainPercents = labels.map((_, idx) => (totalMain > 0 ? (mainValues[idx] || 0) / totalMain : 0));
                          const cmpPercents = labels.map((_, idx) => (totalCmp > 0 ? (cmpValues[idx] || 0) / totalCmp : 0));
                          const boldMatrix = labels.map((_, idx) => {
                            if (!compareHasTwoPies) return { main: false, cmp: false };
                            if (mainPercents[idx] > cmpPercents[idx]) return { main: true, cmp: false };
                            if (cmpPercents[idx] > mainPercents[idx]) return { main: false, cmp: true };
                            return { main: false, cmp: false };
                          });
                          const mainTextTemplates = labels.map((label, idx) => {
                            const base = `${label}<br>%{percent}`;
                            return boldMatrix[idx].main ? `<b>${base}</b>` : base;
                          });
                          const cmpTextTemplates = labels.map((label, idx) => {
                            const base = `${label}<br>%{percent}`;
                            return boldMatrix[idx].cmp ? `<b>${base}</b>` : base;
                          });

                          const pies = [];
                          pies.push(
                            <div key="main-summary" style={{ minWidth: 260, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 12, boxShadow: '0 6px 18px rgba(15,23,42,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <div style={{ fontWeight: 800, marginBottom: 8, textAlign: 'center', color: '#0b1220' }}>{appliedSupervisoraCateg ? `Promedio ${appliedSupervisoraCateg}` : 'Promedio general'}</div>
                              {totalMain > 0 ? (
                                <Plot
                                  data={[{
                                    values: mainValues,
                                    labels,
                                    type: 'pie',
                                    textinfo: 'text',
                                    texttemplate: mainTextTemplates,
                                    textposition: 'outside',
                                    insidetextorientation: 'radial',
                                    automargin: true,
                                    marker: { colors: colorList, line: { color: '#fff', width: 1 } },
                                    hovertemplate: '%{label}: %{value:.2f}€ (%{percent})<extra></extra>'
                                  }]}
                                  layout={{ width: 280, height: 280, margin: { t: 20, b: 20, l: 30, r: 30 }, showlegend: false }}
                                  config={{ displayModeBar: false }}
                                />
                              ) : (
                                <div style={{ width: 240, height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontWeight: 700 }}>Sin datos</div>
                              )}
                            </div>
                          );

                          if (compareHasTwoPies) {
                            pies.push(
                              <div key="compare-summary" style={{ minWidth: 260, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 12, boxShadow: '0 6px 18px rgba(15,23,42,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <div style={{ fontWeight: 800, marginBottom: 8, textAlign: 'center', color: '#0b1220' }}>{`Comparativa: ${compareLabel}`}</div>
                                <Plot
                                  data={[{
                                    values: cmpValues,
                                    labels,
                                    type: 'pie',
                                    textinfo: 'text',
                                    texttemplate: cmpTextTemplates,
                                    textposition: 'outside',
                                    insidetextorientation: 'radial',
                                    automargin: true,
                                    marker: { colors: colorList, line: { color: '#fff', width: 1 } },
                                    hovertemplate: '%{label}: %{value:.2f}€ (%{percent})<extra></extra>'
                                  }]}
                                  layout={{ width: 280, height: 280, margin: { t: 20, b: 20, l: 30, r: 30 }, showlegend: false }}
                                  config={{ displayModeBar: false }}
                                />
                              </div>
                            );
                          }

                          if (pies.length === 0) {
                            return <div key="no-summary" style={{ padding: 20, color: '#94a3b8', fontWeight: 700 }}>Sin datos para resumir.</div>;
                          }
                          return pies;
                        })()}
                      </div>
        <div style={{ position: 'relative' }} ref={categoriasPlotWrapperRef}>
          <Plot data={traces} layout={layout} style={{ width: '100%' }} useResizeHandler={true} config={{ displayModeBar: true, displaylogo: false }}
            onHover={(data) => {
              try {
                lastPlotlyHoverRef.current = Date.now();
                if (!data || !data.points || !data.points.length) return;
                const point = data.points[0];
                let weekIdx = null;
                if (typeof point.pointIndex === 'number') weekIdx = point.pointIndex;
                else if (typeof point.pointNumber === 'number') weekIdx = point.pointNumber;
                if ((weekIdx === null || weekIdx === undefined) && point && point.data && Array.isArray(point.data.x)) {
                  const idx = point.data.x.findIndex(xx => String(xx) === String(point.x));
                  if (idx !== -1) weekIdx = idx;
                }
                if ((weekIdx === null || weekIdx === undefined) && Array.isArray(analWeeks) && analWeeks.length) {
                  const idx = analWeeks.findIndex(w => String(w) === String(point.x));
                  if (idx !== -1) weekIdx = idx;
                }
                if (weekIdx === null || weekIdx === undefined || weekIdx < 0 || weekIdx >= analWeeks.length) {
                  setHoverWeekIndex(null);
                  setHoverPiePosition({ x: 0, y: 0 });
                  return;
                }
                let clientX = null;
                let clientY = null;
                const evt = (point && point.event) ? point.event : data.event;
                if (evt) {
                  if (typeof evt.clientX === 'number' && typeof evt.clientY === 'number') {
                    clientX = evt.clientX;
                    clientY = evt.clientY;
                  } else if (evt.touches && evt.touches[0]) {
                    clientX = evt.touches[0].clientX;
                    clientY = evt.touches[0].clientY;
                  }
                }
                if (clientX === null || clientY === null) {
                  const wrap = categoriasPlotWrapperRef && categoriasPlotWrapperRef.current ? categoriasPlotWrapperRef.current.getBoundingClientRect() : null;
                  if (wrap) {
                    const ratio = (analWeeks.length > 1) ? (weekIdx / (analWeeks.length - 1)) : 0.5;
                    clientX = wrap.left + wrap.width * ratio;
                    clientY = wrap.top + 60;
                  } else {
                    const rect = document.body.getBoundingClientRect();
                    clientX = rect.left + rect.width / 2;
                    clientY = rect.top + 120;
                  }
                }
                setHoverWeekIndex(weekIdx);
                setHoverPiePosition({ x: clientX, y: clientY });
              } catch (e) {
                console.error('Error in onHover', e);
                setHoverWeekIndex(null);
                setHoverPiePosition({ x: 0, y: 0 });
              }
            }}
            onUnhover={() => { lastPlotlyHoverRef.current = Date.now(); setHoverWeekIndex(null); setHoverPiePosition({ x: 0, y: 0 }); }}
          />
                {(hoverWeekIndex !== null && hoverWeekIndex >= 0) && (() => {
                          const resolvedIdx = hoverWeekIndex;
                          if (resolvedIdx >= analWeeks.length) return null;
                          // build raw category arrays and filter out 'Sin etiquetar' when it's zero for both main and compare
                          const rawCats = [
                            { key: 'snacks', label: 'Snacks', main: (s_snacks[resolvedIdx] || 0), cmp: (c_snacks && c_snacks[resolvedIdx]) ? c_snacks[resolvedIdx] : 0 },
                            { key: 'basico', label: 'Básico', main: (s_basico[resolvedIdx] || 0), cmp: (c_basico && c_basico[resolvedIdx]) ? c_basico[resolvedIdx] : 0 },
                            { key: 'proteina', label: 'Proteína', main: (s_proteina[resolvedIdx] || 0), cmp: (c_proteina && c_proteina[resolvedIdx]) ? c_proteina[resolvedIdx] : 0 },
                            { key: 'limpieza', label: 'Limpieza', main: (s_limpieza[resolvedIdx] || 0), cmp: (c_limpieza && c_limpieza[resolvedIdx]) ? c_limpieza[resolvedIdx] : 0 },
                            { key: 'otros', label: 'Sin etiquetar', main: (s_otros[resolvedIdx] || 0), cmp: (c_otros && c_otros[resolvedIdx]) ? c_otros[resolvedIdx] : 0 }
                          ];
                          const hasCompare = (compareActive && c_snacks && Array.isArray(c_snacks));
                          const filteredCats = rawCats.filter(cat => {
                            if (cat.key === 'otros') {
                              return ((cat.main || 0) > 0) || (hasCompare && ((cat.cmp || 0) > 0));
                            }
                            return true;
                          });
                          const vals = filteredCats.map(c => c.main || 0);
                          const labels = filteredCats.map(c => c.label);
                          const cmpVals = hasCompare ? filteredCats.map(c => c.cmp || 0) : null;
                          const boxWidth = hasCompare ? 520 : 280;
                          const pieWidth = hasCompare ? 240 : 260;
                          const hasCoords = hoverPiePosition && Number(hoverPiePosition.x) > 0 && Number(hoverPiePosition.y) > 0;
                          let containerStyle = { position: 'absolute', right: 16, top: 12 };
                          if (hasCoords) {
                            try {
                              const vw = (typeof window !== 'undefined') ? window.innerWidth : 1200;
                              const vh = (typeof window !== 'undefined') ? window.innerHeight : 800;
                              const desiredLeft = hoverPiePosition.x + 20;
                              // const desiredRight = hoverPiePosition.x - (boxWidth + 20);
                              // prefer placing to the right, but if it overflows use left placement
                              let left = Math.min(desiredLeft, Math.max(12, vw - boxWidth - 12));
                              if (left + boxWidth + 12 > vw) {
                                // place to the left of the cursor
                                left = Math.max(12, hoverPiePosition.x - boxWidth - 20);
                              }
                              // clamp top
                              let top = Math.min(Math.max(12, hoverPiePosition.y - 100), Math.max(12, vh - 240));
                              containerStyle = { position: 'fixed', left, top };
                            } catch (e) {
                              containerStyle = { position: 'fixed', left: Math.max(12, hoverPiePosition.x - 200), top: Math.max(12, hoverPiePosition.y - 100) };
                            }
                          }
                          const totalVals = vals.reduce((a,b) => a + b, 0);
                          const cmpTotal = (cmpVals && Array.isArray(cmpVals)) ? cmpVals.reduce((a,b) => a + b, 0) : 0;
                          // decide which values to show: prefer main values, but if they are all zero and compare has data,
                          // show the compare values so the pie is always visible on hover. If both are zero, use tiny epsilons
                          // so Plotly can render a pie (and we will indicate there are no real values via the header).
                          let effectiveVals = vals.slice();
                          let effectiveIsCompare = false;
                          if (!totalVals) {
                            if (hasCompare && cmpTotal > 0) {
                              effectiveVals = cmpVals.slice();
                              effectiveIsCompare = true;
                            } else {
                              effectiveVals = vals.map(v => (v || 0.000001));
                            }
                          }
                          const effectiveTotal = effectiveVals.reduce((a,b) => a + (b || 0), 0) || 0;
                          const mainHoverPerc = effectiveVals.map(v => effectiveTotal ? ((v || 0) / effectiveTotal) : 0);
                          const cmpHoverPerc = (hasCompare && cmpVals) ? cmpVals.map(v => (cmpTotal ? ((v || 0) / cmpTotal) : 0)) : labels.map(() => 0);
                          const hoverBoldMatrix = labels.map((_, idx) => {
                            if (!hasCompare || !cmpVals) return { main: true, cmp: false };
                            if (mainHoverPerc[idx] > cmpHoverPerc[idx]) return { main: true, cmp: false };
                            if (cmpHoverPerc[idx] > mainHoverPerc[idx]) return { main: false, cmp: true };
                            return { main: false, cmp: false };
                          });
                          console.debug && console.debug('Pie render debug', { resolvedIdx, vals, labels, cmpVals, filteredCats, totalVals, cmpTotal, effectiveIsCompare });
                          // helper to compute visualization options for pies: pull, line widths and per-slice text templates
                          const makePieOptions = (arr, emphasizeAllIfNoCompare) => {
                            const tot = arr.reduce((a,b) => a + b, 0) || 1;
                            const perc = arr.map(v => (v / tot * 100));
                            const maxIdx = perc.indexOf(Math.max(...perc));
                            const pull = perc.map((p,i) => {
                              if (emphasizeAllIfNoCompare) return 0.04; // when no compare, slightly pull all so they read better
                              if (p < 3) return 0.06; // small slices pulled out to be visible
                              if (i === maxIdx) return 0.08; // largest emphasized
                              return 0;
                            });
                            const lineWidths = perc.map((p,i) => (emphasizeAllIfNoCompare ? 2 : (i === maxIdx ? 3 : 1)));
                            // create texttemplate per-slice: bold the winner when compare exists, otherwise bold all
                            const textTemplates = perc.map((p,i) => {
                              const base = '%{percent}<br><span style="font-size:12px">(%{value:.2f}€)</span>';
                              if (emphasizeAllIfNoCompare) return `<b>${base}</b>`;
                              return (i === maxIdx) ? `<b>${base}</b>` : base;
                            });
                            const textpos = perc.map(p => (p < 3 ? 'outside' : 'inside'));
                            return { pull, lineWidths, perc, maxIdx, textTemplates, textpos };
                          };
                          const mainPieOpts = makePieOptions(effectiveVals, !hasCompare);
                          const cmpPieOpts = hasCompare ? makePieOptions(cmpVals, false) : null;
                          return (
                            <div style={{ ...containerStyle, width: boxWidth, background: '#fff', border: '2px solid #ddd', borderRadius: 12, padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 9999, pointerEvents: 'none' }}>
                              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, textAlign: 'center', color: '#0b1220' }}>Semana {formatDate(analWeeks[resolvedIdx])}{hasCompare ? ` — ${compareLabel || ''}` : ''}</div>
                              <div style={{ display: 'flex', gap: 12, justifyContent: hasCompare ? 'space-between' : 'center', alignItems: 'flex-start' }}>
                                <div style={{ width: pieWidth }}>
                                  {(() => {
                                    // build per-slice display text (percent + value) with bold for winner or all-bold when no compare
                                    const mainPieText = mainPieOpts.perc.map((p, i) => {
                                      const val = effectiveVals[i] || 0;
                                      const base = `${p.toFixed(1)}%\n(${Number(val).toFixed(2)}€)`;
                                      const shouldBold = Boolean(hoverBoldMatrix[i]?.main);
                                      return shouldBold ? `<b>${base}</b>` : base;
                                    });
                                    return (
                                      <Plot
                                        data={[{
                                          values: effectiveVals,
                                          labels,
                                          type: 'pie',
                                          textinfo: 'text',
                                          text: mainPieText,
                                          textposition: mainPieOpts.textpos,
                                          texttemplate: '%{text}',
                                          insidetextorientation: 'radial',
                                          marker: { colors: filteredCats.map(fc => (CATEGORY_COLORS[fc.label] ? CATEGORY_COLORS[fc.label].solid : 'rgba(200,200,200,0.9)')), line: { color: '#ffffff', width: mainPieOpts.lineWidths } },
                                          pull: mainPieOpts.pull,
                                          textfont: { size: 16, color: '#0b1220' },
                                          hovertemplate: '%{label}: %{value:.2f}€ (%{percent})<extra></extra>'
                                        }]}
                                        layout={{ width: pieWidth, height: 220, margin: { t: 0, b: 0, l: 0, r: 0 }, showlegend: false, automargin: true }}
                                        config={{ displayModeBar: false, staticPlot: true }}
                                      />
                                    );
                                  })()}
                                </div>
                                {hasCompare && (
                                  <div style={{ width: pieWidth }}>
                                    {(() => {
                                      const cmpPieText = (cmpPieOpts && cmpVals) ? cmpPieOpts.perc.map((p, i) => {
                                        const val = (cmpVals && cmpVals[i]) ? cmpVals[i] : 0;
                                        const base = `${p.toFixed(1)}%\n(${Number(val).toFixed(2)}€)`;
                                        const shouldBold = Boolean(hoverBoldMatrix[i]?.cmp);
                                        return shouldBold ? `<b>${base}</b>` : base;
                                      }) : (cmpVals ? cmpVals.map(v => `${(v/ (cmpVals.reduce((a,b)=>a+b,0)||1)*100).toFixed(1)}%\n(${Number(v||0).toFixed(2)}€)`) : []);
                                      return (
                                        <Plot
                                          data={[{
                                            values: cmpVals,
                                            labels,
                                            type: 'pie',
                                            textinfo: 'text',
                                            text: cmpPieText,
                                            textposition: (cmpPieOpts ? cmpPieOpts.textpos : 'inside'),
                                            texttemplate: '%{text}',
                                            insidetextorientation: 'radial',
                                            marker: { colors: filteredCats.map(fc => (CATEGORY_COLORS[fc.label] ? CATEGORY_COLORS[fc.label].solid : 'rgba(200,200,200,0.9)')), line: { color: '#ffffff', width: (cmpPieOpts ? cmpPieOpts.lineWidths : []) } },
                                            pull: (cmpPieOpts ? cmpPieOpts.pull : []),
                                            textfont: { size: 16, color: '#0b1220' },
                                            hovertemplate: '%{label}: %{value:.2f}€ (%{percent})<extra></extra>'
                                          }]}
                                          layout={{ width: pieWidth, height: 220, margin: { t: 0, b: 0, l: 0, r: 0 }, showlegend: false, automargin: true }}
                                          config={{ displayModeBar: false, staticPlot: true }}
                                        />
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 24 }}>
        <BotonVolver fallback="/admin" />
        <h2 style={{ textAlign: 'center', fontSize: 28, fontWeight: 'bold', color: '#0b1220' }}>Análisis de Datos</h2>
      <SmallTabs tabs={["Gasto semanal por alumno Rutas 1 y 2", "Top 10 pisos con más gasto", "Distribución del gasto semanal por supervisora", "Porcentaje de gasto por etiquetas de productos", "📊 Comparativa con el año pasado"]} active={activeTab} onChange={setActiveTab} />

      {activeTab === 4 && <ComparativaAnioPasado />}

      {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 1100, background: 'linear-gradient(90deg,#eef2ff 0%, #e0f7fa 50%, #f3e8ff 100%)', border: '1px solid #c7b9ff', padding: 18, borderRadius: 12, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between', boxShadow: '0 10px 30px rgba(99,102,241,0.06)', marginBottom: 18 }}>
              <div style={{ minWidth: 360, position: 'relative' }} ref={dropdownRef}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, color: '#0b1220' }}>Supervisoras</label>
                <div>
                  <button onClick={() => setDropdownOpen(d => !d)} style={{ width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 10, border: '1px solid #e6eefc', background: '#fff', cursor: 'pointer', boxShadow: '0 6px 18px rgba(2,6,23,0.04)', fontWeight:700 }}>
                    {activeTab === 2 ? (selectedSupervisorSingle ? selectedSupervisorSingle : 'Seleccione una supervisora') : (selectedSupervisoras.length === 0 ? 'Todos' : (selectedSupervisoras.length > 2 ? `${selectedSupervisoras.slice(0,2).join(', ')} +${selectedSupervisoras.length-2}` : selectedSupervisoras.join(', ')))}
                    <span style={{ float: 'right', opacity: 0.7 }}>▾</span>
                  </button>
                </div>

                {dropdownOpen && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: 360, background: '#fff', border: '1px solid #e6e9ef', borderRadius: 8, boxShadow: '0 14px 40px rgba(2,6,23,0.08)', zIndex: 2200 }}>
                    {activeTab === 2 ? (
                      <div style={{ padding: 10, borderBottom: '1px solid #f3f4f6', fontWeight: 700 }}>Selecciona una supervisora</div>
                    ) : (
                      <div style={{ padding: 10, borderBottom: '1px solid #f3f4f6' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={selectedSupervisoras.length === 0} onChange={() => setSelectedSupervisoras([])} />
                          <strong>Todos</strong>
                        </label>
                      </div>
                    )}
                    <div style={{ maxHeight: 220, overflowY: 'auto', padding: 8 }}>
                      {users.length === 0 ? <div style={{ padding: 8, color: '#666' }}>No hay supervisoras</div> : users.map(u => (
                        activeTab === 2 ? (
                          <label key={u} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer' }}>
                            <input type="radio" name="supervisor-single" checked={selectedSupervisorSingle === u} onChange={(e) => { if (e.target.checked) setSelectedSupervisorSingle(u); }} />
                            <span style={{ fontSize: 13 }}>{u}</span>
                          </label>
                        ) : (
                          <label key={u} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={selectedSupervisoras.includes(u)} onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedSupervisoras(prev => {
                                if (checked) {
                                  return Array.from(new Set([...(prev || []), u]));
                                }
                                return (prev || []).filter(x => x !== u);
                              });
                            }} />
                            <span style={{ fontSize: 13 }}>{u}</span>
                          </label>
                        )
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 8, borderTop: '1px solid #f3f4f6' }}>
                      {activeTab === 2 ? (
                        <button onClick={() => { setSelectedSupervisorSingle(''); setDropdownOpen(false); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}>Limpiar</button>
                      ) : (
                        <button onClick={() => { setSelectedSupervisoras([]); setDropdownOpen(false); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}>Todos</button>
                      )}
                      <button onClick={() => setDropdownOpen(false)} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'linear-gradient(90deg,#2563eb,#7c3aed)', color: '#fff' }}>Cerrar</button>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {activeTab === 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
                    <label style={{ marginBottom: 6, fontSize: 13, color: '#0f172a', fontWeight: 700 }}>Top</label>
                    <input type="number" value={topN} min={1} max={100} onChange={e => {
                        const v = e.target.value;
                        if (v === '') { setTopN(''); return; }
                        let n = parseInt(v, 10);
                        if (isNaN(n) || n < 1) return;
                        if (n > 100) n = 100;
                        setTopN(n);
                      }} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #e6eefc', width: 120 }} />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: 6, fontSize: 13, color: '#0f172a', fontWeight: 700 }}>Desde</label>
                  <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #e6eefc', boxShadow: 'inset 0 1px 2px rgba(2,6,23,0.03)' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: 6, fontSize: 13, color: '#0f172a', fontWeight: 700 }}>Hasta</label>
                  <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #e6eefc', boxShadow: 'inset 0 1px 2px rgba(2,6,23,0.03)' }} />
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button onClick={() => { 
                      // apply currently drafted supervisoras; for tab 3 use single selection
                      if (activeTab === 2) {
                        const sup = selectedSupervisorSingle ? [selectedSupervisorSingle] : [];
                        applyFilters({ supervisora: sup });
                      } else {
                        applyFilters({ supervisora: selectedSupervisoras });
                      }
                      setDropdownOpen(false);
                    }} style={{ padding: '12px 18px', background: 'linear-gradient(90deg,#06b6d4,#3b82f6)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, boxShadow: '0 12px 32px rgba(59,130,246,0.18)' }}>Aplicar</button>
                      <button onClick={handleClearFilters} style={{ padding: '12px 18px', background: '#fff7ed', color: '#92400e', border: '1px solid #ffd8a8', borderRadius: 12, fontWeight: 800, boxShadow: '0 8px 20px rgba(245,158,11,0.08)', cursor: 'pointer' }}>🧹 Limpiar filtros</button>
                </div>
              </div>
            </div>
          </div>

          {/* Info: la media ahora se calcula con las supervisoras marcadas en configuración */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 12, alignItems: 'center', fontSize: 13, color: '#475569' }}>
            <span>📊 Media calculada con {supervisorasMedia.length} supervisora{supervisorasMedia.length === 1 ? '' : 's'} configurada{supervisorasMedia.length === 1 ? '' : 's'}</span>
            <button
              onClick={() => navigate('/admin/configuracion-servicios/supervisoras-media')}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e6eefc', background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 12, color: '#3b82f6' }}
            >
              Configurar
            </button>
          </div>

          {/* leyenda con medias generales */}
          {activeTab === 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
              {/* MEDIA entry first */}
              <div key="__media__" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: '#fff', border: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ width: 14, height: 14, background: '#111', borderRadius: 3 }} />
                <div style={{ fontSize: 15, fontWeight: 900 }}>MEDIA</div>
                <div style={{ marginLeft: 8, fontSize: 14, color: '#333', fontWeight: 800 }}>{meanTotal !== null ? meanTotal.toFixed(2) : '—'}</div>
              </div>

              <button
                onClick={() => {
                  const v = !compararPasado;
                  setCompararPasado(v);
                  applyFilters({ comparar: v, supervisora: appliedSupervisoras, start_date: appliedStartDate, end_date: appliedEndDate });
                }}
                style={{
                  padding: '9px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 800, fontSize: 14,
                  border: compararPasado ? 'none' : '1px solid #94a3b8',
                  background: compararPasado ? '#475569' : '#fff', color: compararPasado ? '#fff' : '#334155',
                }}
              >
                {compararPasado ? '✕ Quitar año pasado' : '📅 Comparar con el año pasado'}
              </button>

              {analisisData && analisisData.series && analisisData.series.slice(0, 50).map((s, i) => {
                const vals = (s.values || []).filter(v=> v !== null && v !== undefined);
                const avg = vals.length ? (vals.reduce((a,b)=>a+b,0)/vals.length) : null;
                const color = colorMap[s.name] || COLORS[i % COLORS.length];
                  return (
                  <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: '#fff', border: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ width: 14, height: 14, background: color, borderRadius: 3 }} />
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{s.name}</div>
                    <div style={{ marginLeft: 6, fontSize: 13, color: '#333', fontWeight: 700 }}>{avg !== null ? avg.toFixed(2) : '—'}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 0 && (
        <div>
          {loadingAnalisis && <div>Cargando...</div>}
          {analisisError && <div style={{ color: 'red' }}>{analisisError}</div>}
          {analisisData && (
            <div>
              <div style={{ marginBottom: 8, fontSize: 13, color: '#555' }}>Grafica: gasto promedio por alumno (por semana). Cada linea = supervisora. Línea negra = media.</div>
              {/* filter series client-side according to multi-select */}
              {(() => {
                const seriesToShow = (analisisData.series || []).filter(s => {
                  if (!appliedSupervisoras || appliedSupervisoras.length === 0) return true;
                  return appliedSupervisoras.includes(s.name);
                });
                // "Comparar con el año pasado": vista de una en una (Media y luego cada supervisora)
                if (compararPasado && analisisData.pasado && meanSeriesDisplayed) {
                  return <ComparadorAnioPasado weeks={analisisData.weeks} mean={meanSeriesDisplayed} series={seriesToShow} pasado={analisisData.pasado} colorMap={colorMap} />;
                }
                return <LineChart weeks={analisisData.weeks} series={seriesToShow} meanSeries={meanSeriesDisplayed} colorMap={colorMap} />;
              })()}
            </div>
          )}
        </div>
      )}

      {activeTab === 1 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <div style={{ width: '100%', maxWidth: 1100, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div>{`Top ${appliedTopN || 10} pisos con más gasto`}</div>
                <div style={{ fontSize: 18, color: '#374151', fontWeight: 700 }}>→ Media: {selectedMean !== null && selectedMean !== undefined ? Number(selectedMean).toFixed(2) : '—'}</div>
                <div style={{ fontSize: 18, color: '#374151', fontWeight: 700 }}>→ Media semanal por alumno: {selectedMeanWeekly !== null && selectedMeanWeekly !== undefined ? Number(selectedMeanWeekly).toFixed(2) : '—'}</div>
              </div>
              <div />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 1100, background: '#fff', border: '1px solid #eef2f7', padding: 12, borderRadius: 10 }}>
              {loadingTopPisos && <div>Cargando top pisos...</div>}
              {topPisosError && <div style={{ color: 'red' }}>{topPisosError}</div>}
              {!loadingTopPisos && !topPisosError && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee', background: '#8fe6ecff' }}>
                      <th style={{ padding: '8px 6px', width: 56 }}>#</th>
                      <th style={{ padding: '8px 6px' }}>Piso</th>
                      <th style={{ padding: '8px 6px' }}>Sexo</th>
                      <th style={{ padding: '8px 6px' }}>Supervisora</th>
                      <th style={{ padding: '8px 6px' }}>Gasto total por alumno</th>
                      <th style={{ padding: '8px 6px' }}>Gasto semanal por alumno</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPisos.slice(0, (appliedTopN && Number(appliedTopN)) || 10).map((p, i) => {
                      const weeksCount = (analisisData && Array.isArray(analisisData.weeks)) ? analisisData.weeks.length : 1;
                      const weeklyAvg = (p.gasto_por_alumno !== null && p.gasto_por_alumno !== undefined && weeksCount > 0) ? (Number(p.gasto_por_alumno) / weeksCount) : null;
                      return (
                        <tr key={p.id_piso} style={{ borderBottom: '1px solid #fafafa' }}>
                          <td style={{ padding: '10px 6px', fontWeight: 800 }}>{i+1}</td>
                          <td style={{ padding: '10px 6px', fontWeight: 700 }}>{p.abreviatura_piso || `#${p.id_piso}`}</td>
                          <td style={{ padding: '10px 6px', fontWeight: 800 }}>{p.sexo || '—'}</td>
                          <td style={{ padding: '10px 6px' }}>{p.user_supervisora || '—'}</td>
                          <td style={{ padding: '10px 6px', fontWeight: 800 }}>{p.gasto_por_alumno !== null && p.gasto_por_alumno !== undefined ? Number(p.gasto_por_alumno).toFixed(2) : '—'}</td>
                          <td style={{ padding: '10px 6px', fontWeight: 700 }}>{weeklyAvg !== null ? weeklyAvg.toFixed(2) : '—'}</td>
                        </tr>
                      );
                    })}
                    {topPisos.length === 0 && (
                      <tr><td colSpan={6} style={{ padding: 12, color: '#666' }}>No hay datos</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 2 && (
        <div>
          

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 1100, background: '#fff', border: '1px solid #eef2f7', padding: 12, borderRadius: 10 }}>
              {loadingDistrib && <div>Cargando distribución...</div>}
              {distribError && <div style={{ color: 'red' }}>{distribError}</div>}
              {!loadingDistrib && !distribError && distribData && distribData.rows && (
                (() => {
                  // prepare plotly data: box per week using categorical x
                  const rows = distribData.rows || [];
                  // filter rows for the currently APPLIED supervisora only (do not react to the draft selector)
                  const sup = (appliedSupervisoras && appliedSupervisoras.length === 1) ? appliedSupervisoras[0] : null;
                  const filtered = sup ? rows.filter(r=> r.user_supervisora === sup) : rows;

                  // build unique piso map to compute unique n_estudiantes per sexo
                  const pisoMap = {}; // id_piso -> { sexo, n_estudiantes }
                  filtered.forEach(r => {
                    const pid = r.id_piso;
                    if (!pid) return;
                    if (!pisoMap[pid]) {
                      pisoMap[pid] = { sexo: (r.sexo||'').toUpperCase(), n: Number(r.n_estudiantes || 0) };
                    }
                  });
                  let maleCount = 0;
                  let femaleCount = 0;
                  Object.keys(pisoMap).forEach(k => {
                    const it = pisoMap[k];
                    if (it.sexo === 'H') maleCount += (it.n || 0);
                    else if (it.sexo === 'M') femaleCount += (it.n || 0);
                  });

                  // use analisisData.weeks if available so format/frequency match pestaña 1
                  const weeks = (analisisData && Array.isArray(analisisData.weeks) && analisisData.weeks.length) ? analisisData.weeks : Array.from(new Set(filtered.map(r=>r.week_start))).sort();

                  // build arrays of point x (week), y (value) and hover text (abreviatura), separated by sexo
                  const pointX_H = [];
                  const pointY_H = [];
                  const pointText_H = [];
                  const pointX_M = [];
                  const pointY_M = [];
                  const pointText_M = [];
                  filtered.forEach(r => {
                    if (r.gasto_por_alumno === null || r.gasto_por_alumno === undefined) return;
                    const sexo = (r.sexo || '').toUpperCase();
                    if (sexo === 'H') {
                      pointX_H.push(r.week_start);
                      pointY_H.push(Number(r.gasto_por_alumno));
                      pointText_H.push(r.abreviatura_piso || (`#${r.id_piso}`));
                    } else if (sexo === 'M') {
                      pointX_M.push(r.week_start);
                      pointY_M.push(Number(r.gasto_por_alumno));
                      pointText_M.push(r.abreviatura_piso || (`#${r.id_piso}`));
                    }
                  });

                  // compute mean per week using the global mean from analisisData (same as pestaña 1)
                  const meanX = [];
                  const meanY = [];
                  const analWeeks = (analisisData && Array.isArray(analisisData.weeks) && analisisData.weeks.length) ? analisisData.weeks : weeks;
                  const analMeanVals = (meanSeriesDisplayed && Array.isArray(meanSeriesDisplayed.values)) ? meanSeriesDisplayed.values : [];
                  weeks.forEach(w => {
                    // align week w with index in analWeeks
                    const idx = analWeeks.indexOf(w);
                    meanX.push(w);
                    if (idx >= 0 && analMeanVals[idx] !== null && analMeanVals[idx] !== undefined) meanY.push(Number(analMeanVals[idx]));
                    else meanY.push(null);
                  });

                  const traces = [];
                  // Hombres
                  if (pointY_H.length > 0) {
                    traces.push({ type: 'box', x: pointX_H, y: pointY_H, name: 'Hombres', boxpoints: 'all', jitter: 0.5, pointpos: 0, marker: { color: '#1f77b4', opacity: 0.6 }, text: pointText_H, hovertemplate: '%{text}<br>%{y:.2f}<extra></extra>' });
                  }
                  // Mujeres (rosita)
                  if (pointY_M.length > 0) {
                    traces.push({ type: 'box', x: pointX_M, y: pointY_M, name: 'Mujeres', boxpoints: 'all', jitter: 0.5, pointpos: 0, marker: { color: '#ff9ab3', opacity: 0.6 }, text: pointText_M, hovertemplate: '%{text}<br>%{y:.2f}<extra></extra>' });
                  }
                  // global mean line
                  traces.push({ x: meanX, y: meanY, mode: 'lines+markers', name: 'Media (todas supervisoras)', line: { color: '#111', width: 3 }, marker: { size: 6, color: '#111' }, hovertemplate: '%{y:.2f}<extra></extra>' });
                  const dataPlot = traces;
                  // prefer analWeeks for tick ordering/format to match pestaña 1
                  const layout = {
                    title: `Distribución por piso - ${sup || 'Todas las supervisoras'}`,
                    xaxis: {
                      title: 'Semana',
                      tickangle: -45,
                      tickmode: 'array',
                      tickvals: analWeeks,
                      ticktext: analWeeks.map(w => formatDate(w)),
                      categoryorder: 'array',
                      categoryarray: analWeeks
                    },
                    yaxis: { title: 'Gasto por alumno' },
                    boxmode: 'group',
                    height: 520
                  };

                  return (
                    <>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 10, justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderRadius: 10, background: '#1f77b4', color: '#fff' }}>
                          <div style={{ fontSize: 12, opacity: 0.9 }}>Hombres</div>
                          <div style={{ fontSize: 28, fontWeight: 900 }}>{maleCount}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderRadius: 10, background: '#ff9ab3', color: '#fff' }}>
                          <div style={{ fontSize: 12, opacity: 0.9 }}>Mujeres</div>
                          <div style={{ fontSize: 28, fontWeight: 900 }}>{femaleCount}</div>
                        </div>
                      </div>
                      <Plot data={dataPlot} layout={layout} style={{ width: '100%' }} useResizeHandler={true} />
                    </>
                  );
                })()
              )}
              {!loadingDistrib && !distribError && (!distribData || (distribData.rows && distribData.rows.length === 0)) && (
                <div style={{ padding: 12, color: '#666' }}>No hay datos para la supervisora y rango seleccionados.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NEW TAB 3: Stacked Area Chart by Category */}
      {activeTab === 3 && (
        <div>
          {/* Filters specific to tab 3 */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <div style={{ width: '100%', maxWidth: 1200, background: 'linear-gradient(90deg,#eef2ff 0%, #e0f7fa 50%, #f3e8ff 100%)', border: '1px solid #c7b9ff', padding: 18, borderRadius: 12, display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', boxShadow: '0 10px 30px rgba(99,102,241,0.06)' }}>
              
              <div style={{ minWidth: 200 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, color: '#0b1220' }}>Supervisora</label>
                <select 
                  value={selectedSupervisoraCateg} 
                  onChange={e => {
                    setSelectedSupervisoraCateg(e.target.value);
                    setSelectedPisosCateg([]);
                  }}
                  style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e6eefc', background: '#fff', width: '100%', fontWeight: 600 }}
                >
                  <option value="">Todas</option>
                  {users.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              
              {selectedSupervisoraCateg && availablePisosCateg.length > 0 && (
                <div style={{ minWidth: 220 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, color: '#0b1220' }}>Pisos</label>
                  <select 
                    multiple
                    value={selectedPisosCateg}
                    onChange={e => {
                      const opts = Array.from(e.target.selectedOptions);
                      setSelectedPisosCateg(opts.map(o => o.value));
                    }}
                    style={{ padding: '8px', borderRadius: 8, border: '1px solid #e6eefc', background: '#fff', minHeight: 90, width: '100%' }}
                  >
                    {availablePisosCateg.map(p => (
                      <option key={p.id_piso} value={p.id_piso}>
                        {p.abreviatura_piso || `#${p.id_piso}`}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>Ctrl+Click para varios</div>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 700, color: '#0b1220' }}>Desde</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e6eefc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 700, color: '#0b1220' }}>Hasta</label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e6eefc' }} />
                </div>
              </div>
              
              <div style={{ minWidth: 240 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, color: '#0b1220' }}>Comparar con</label>
                <select value={compareMode} onChange={e => setCompareMode(e.target.value)} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e6eefc', background: '#fff', width: '100%', fontWeight: 600 }}>
                  <option value="none">No comparar</option>
                  <option value="media">Media</option>
                  <option value="supervisora">Otra supervisora</option>
                  <option value="piso">Otro piso</option>
                </select>
              </div>
              
              {compareMode === 'supervisora' && (
                <div style={{ minWidth: 200 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, color: '#0b1220' }}>Supervisora</label>
                  <select value={compareSupervisora} onChange={e => setCompareSupervisora(e.target.value)} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e6eefc', background: '#fff', width: '100%' }}>
                    <option value="">Seleccionar...</option>
                    {users.filter(u => u !== selectedSupervisoraCateg).map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              )}
              
              {compareMode === 'piso' && availablePisosCateg.length > 0 && (
                <div style={{ minWidth: 200 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, color: '#0b1220' }}>Piso</label>
                  <select value={comparePiso} onChange={e => setComparePiso(e.target.value)} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e6eefc', background: '#fff', width: '100%' }}>
                    <option value="">Seleccionar...</option>
                    {availablePisosCateg.filter(p => !selectedPisosCateg.includes(String(p.id_piso))).map(p => (
                      <option key={p.id_piso} value={p.id_piso}>
                        {p.abreviatura_piso || `#${p.id_piso}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginLeft: 'auto', alignItems: 'center' }}>
                <button 
                  onClick={() => {
                    // set applied filters for title and then fetch
                    setAppliedSupervisoraCateg(selectedSupervisoraCateg || '');
                    setAppliedPisosCateg(Array.isArray(selectedPisosCateg) ? selectedPisosCateg.slice() : []);
                    // fetch using the drafted values
                    fetchCategorias({ 
                      supervisora: selectedSupervisoraCateg, 
                      pisos: selectedPisosCateg,
                      start_date: startDate,
                      end_date: endDate
                    });
                  }}
                  style={{ padding: '12px 20px', background: 'linear-gradient(90deg,#06b6d4,#3b82f6)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, cursor: 'pointer', boxShadow: '0 8px 24px rgba(6,182,212,0.3)' }}
                >
                  Aplicar
                </button>
                <button
                  onClick={() => {
                    handleClearFilters();
                    fetchCategorias({ supervisora: '', pisos: [], start_date: '', end_date: '' });
                  }}
                  style={{ padding: '12px 20px', background: '#fff7ed', color: '#92400e', border: '1px solid #ffd8a8', borderRadius: 12, fontWeight: 800, cursor: 'pointer', boxShadow: '0 8px 20px rgba(245,158,11,0.08)' }}
                >
                  🧹 Limpiar filtros
                </button>
              </div>

            </div>
          </div>

          {/* Chart area */}
          <div style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
            <div style={{ width: '100%', maxWidth: 1200, background: '#fff', border: '1px solid #eef2f7', padding: 16, borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
              {loadingCategorias && <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Cargando...</div>}
              {categoriasError && <div style={{ color: 'red', padding: 12 }}>{categoriasError}</div>}
              {!loadingCategorias && !categoriasError && categoriasData && categoriasData.weeks && categoriasData.weeks.length > 0 && renderCategoriasChart()}
                    {!loadingCategorias && !categoriasError && (!categoriasData || !categoriasData.weeks || categoriasData.weeks.length === 0) && (
                <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>No hay datos disponibles para los filtros seleccionados.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalisisDatos;
